import * as Sentry from '@sentry/browser'
import { isEmpty } from 'lodash-es'
import {
  fetchOrgmap,
  fetchLayerAndDepth,
  fetchLayerAndSpanOfControl,
  fetchDistribution,
  fetchSpanOfControl,
  fetchAverageSpanOfControl,
  fetchIcicle,
  fetchCostDistribution,
  fetchGrowthDistribution,
  fetchGrowthDistributionDates,
  fetchPyramidChart,
  fetchStackedHeadcountCost,
  fetchSkillsHeatmap,
  fetchLayersTable
} from '@/services/hub-api.service'
import {
  sortDataByGivenOrder,
  sortHeadcountDistributionAxis,
  sortHeadcountCostDistributionAxis,
  sortOrgMapData,
  sortSpanOfControlData,
  sortStackedHeadcountCostData,
  sortLayersTableRows,
  sortLayersTableColumns,
  reorderLayerAndSpanOfControlXAxis,
  reorderLayerAndSpanOfControlYAxis,
  sortLayerAndDepthXAxis,
  sortLayerAndDepthYAxis,
  sortSkillsHeatmapYAxis,
  sortSkillsHeatmapXAxis
} from '@/utils/Utils'
import { applyCorrectness } from '@/utils/workforceHubChartConfig/pyramidChart'

import { HUB_DICT } from '@/lib/WorkforceHubCharts'
import { logger } from '@/utils/logger'

const isCanceledError = (error) =>
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError'

const supportsAbortController = typeof AbortController !== 'undefined'
const inflightRequests = new Map()

const beginRequest = (key, externalSignal) => {
  if (externalSignal || !supportsAbortController) {
    return {
      signal: externalSignal,
      complete: () => {}
    }
  }

  const activeController = inflightRequests.get(key)
  activeController?.abort()

  const controller = new AbortController()
  inflightRequests.set(key, controller)

  return {
    signal: controller.signal,
    complete: () => {
      if (inflightRequests.get(key) === controller) {
        inflightRequests.delete(key)
      }
    }
  }
}

const getRequestKey = (action, boardId) => `${action}:${boardId}`

const getDefaultState = () => ({
  layerAndDepth: {},
  layerAndSpanOfControl: {},
  orgMap: {},
  headcountDistribution: {},
  headcountCostDistribution: {},
  headcountGrowthDistribution: {},
  spanOfControl: {},
  averageSpanOfControl: {},
  icicleChart: {},
  pyramid: [],
  stackedHeadcountCost: {},
  skillsHeatmap: {},
  layersTable: {}
})

const state = {
  dataById: {}
}

/**
 * Sets the state with data and options for a specific hub.
 *
 * @param {object} _state - The state object to be modified.
 * @param {object} data - The data to be assigned to the state's hub.
 * @param {string} hub - The hub identifier within the state.
 */
const setStateWithOptions = (_state, data, hub) => {
  _state[hub].dataByOptions = data.data
  _state[hub].options = data.options
}

const mutations = {
  setLayerAndDepth(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.LAYER_AND_DEPTH)

    _state.dataById[boardId].layerAndDepth.noManagerSubordinateCount =
      data.noManagerSubordinateCount
  },
  setLayerAndSpanOfControl(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.LAYER_AND_SPAN_OF_CONTROL)

    _state.dataById[boardId].layerAndSpanOfControl.noManagerSubordinateCount =
      data.noManagerSubordinateCount
  },
  setHeadcountDistribution(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.HEADCOUNT_DISTRIBUTION)

    _state.dataById[boardId].headcountDistribution.noManagerSubordinateCount =
      data.noManagerSubordinateCount
  },
  setHeadcountCostDistribution(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.HEADCOUNT_COST_DISTRIBUTION)

    _state.dataById[boardId].headcountCostDistribution.noManagerSubordinateCount =
      data.noManagerSubordinateCount
  },
  setHeadcountGrowthDistribution(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.HEADCOUNT_GROWTH_DISTRIBUTION)
    _state.dataById[boardId][HUB_DICT.HEADCOUNT_GROWTH_DISTRIBUTION].exportableData =
      data.exportableData
  },
  setHeadcountGrowthDistributionDates(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    _state.dataById[boardId][HUB_DICT.HEADCOUNT_GROWTH_DISTRIBUTION].dates = data
  },
  setSpanOfControl(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.SPAN_OF_CONTROL)
  },
  setAverageSpanOfControl(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.AVERAGE_SPAN_OF_CONTROL)
  },
  setOrgMap(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    _state.dataById[boardId].orgMap = data
  },
  setPyramidChart(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    _state.dataById[boardId].pyramid = data
  },
  setIcicleChart(_state, { boardId, data, groups, exportableData }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    _state.dataById[boardId].icicleChart = {
      data: Object.freeze(data),
      groups,
      exportableData
    }
  },
  setStackedHeadcountCost(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.STACKED_HEADCOUNT_COST)

    _state.dataById[boardId].stackedHeadcountCost.noManagerSubordinateCount =
      data.noManagerSubordinateCount
  },
  setSkillsHeatmap(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.SKILLS_HEATMAP)
    _state.dataById[boardId][HUB_DICT.SKILLS_HEATMAP].exportableData = data.exportableData
  },
  setLayersTable(_state, { boardId, data }) {
    if (!_state.dataById[boardId]) {
      _state.dataById[boardId] = getDefaultState()
    }
    setStateWithOptions(_state.dataById[boardId], data, HUB_DICT.LAYERS_TABLE)
    _state.dataById[boardId].layersTable.noManagerSubordinateCount = data.noManagerSubordinateCount
  },

  //reset
  resetOrgMap(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].orgMap = {}
    }
  },
  resetLayerAndDepth(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].layerAndDepth = {}
    }
  },
  resetLayerAndSpanOfControl(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].layerAndSpanOfControl = {}
    }
  },
  resetHeadcountDistribution(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].headcountDistribution = {}
    }
  },
  resetHeadcountCostDistribution(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].headcountCostDistribution = {}
    }
  },
  resetHeadcountGrowthDistribution(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].headcountGrowthDistribution = {}
    }
  },
  resetSpanOfControl(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].spanOfControl = {}
    }
  },
  resetAverageSpanOfControl(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].averageSpanOfControl = {}
    }
  },
  resetIcicleChart(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].icicleChart = {}
    }
  },
  resetPyramidChart(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].pyramid = []
    }
  },
  resetStackedHeadcountCost(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].stackedHeadcountCost = {}
    }
  },
  resetSkillsHeatmap(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].skillsHeatmap = {}
    }
  },
  resetLayersTable(_state, boardId) {
    if (_state.dataById[boardId]) {
      _state.dataById[boardId].layersTable = {}
    }
  }
}

const getters = {
  // layer and depth
  getLayerAndDepth: (_state) => (boardId) => {
    return _state.dataById[boardId]?.layerAndDepth
  },

  getLayerAndDepthOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.layerAndDepth?.options || [],

  // layer and span of control
  getLayerAndSpanOfControl: (_state) => (boardId) => {
    return _state.dataById[boardId]?.layerAndSpanOfControl
  },

  getLayerAndSpanOfControlOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.layerAndSpanOfControl?.options || [],

  // headcount distribution
  getHeadcountDistribution: (_state) => (boardId) => {
    return _state.dataById[boardId]?.headcountDistribution
  },

  getHeadcountDistributionOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.headcountDistribution?.options || [],

  // headcount cost distribution
  getHeadcountCostDistribution: (_state) => (boardId) => {
    return _state.dataById[boardId]?.headcountCostDistribution
  },
  getHeadcountCostDistributionOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.headcountCostDistribution?.options || [],

  // headcount growth distribution
  getHeadcountGrowthDistribution:
    (_state) =>
    (boardId, { filter }) =>
      _state.dataById[boardId]?.headcountGrowthDistribution?.dataByOptions?.[filter] || [],
  getHeadcountGrowthDistributionOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.headcountGrowthDistribution?.options || [],
  getHeadcountGrowthDistributionDates: (_state) => (boardId) =>
    _state.dataById[boardId]?.headcountGrowthDistribution?.dates || {},
  getHeadcountGrowthDistributionExportableData: (_state) => (boardId) =>
    _state.dataById[boardId]?.headcountGrowthDistribution?.exportableData || [],

  // span of control distribution
  getSpanOfControl:
    (_state) =>
    (boardId, { filter }) => {
      const prettifiedFilter = filter === 'Layer' ? 'Org Level' : filter
      return _state.dataById[boardId]?.spanOfControl?.dataByOptions?.[prettifiedFilter] || {}
    },
  getSpanOfControlOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.spanOfControl?.options || [],

  // span of control
  getAverageSpanOfControl:
    (_state) =>
    (boardId, { filter }) => {
      const prettifiedFilter = filter === 'Layer' ? 'Org Level' : filter
      return _state.dataById[boardId]?.averageSpanOfControl?.dataByOptions?.[prettifiedFilter] || {}
    },
  getAverageSpanOfControlOptions: (_state) => (boardId) =>
    _state.dataById[boardId]?.averageSpanOfControl?.options || [],

  // icicle
  getIcicleChartGroup: (_state) => (boardId) => _state.dataById[boardId]?.icicleChart?.groups || [],
  getIcicleChart: (_state) => (boardId) => _state.dataById[boardId]?.icicleChart.data || {},
  getIcicleChartExportableData: (_state) => (boardId) =>
    _state.dataById[boardId]?.icicleChart?.exportableData || [],
  isIcicleChartAvailable: (_state, _getters) => (boardId) =>
    !isEmpty(_getters.getIcicleChart(boardId)),

  // pyramid
  getPyramidChart: (_state) => (boardId) => _state.dataById[boardId]?.pyramid || [],

  // org map
  getOrgMapByOptions:
    (_state) =>
    (boardId, { filter }) =>
      _state.dataById[boardId]?.orgMap?.dataset?.[filter] || [],
  getOrgMapOptions: (_state) => (boardId) => _state.dataById[boardId]?.orgMap?.options || [],
  isOrgMapAvailable:
    (_state, _getters) =>
    (boardId, { filter }) => {
      return _getters.getOrgMapByOptions(boardId, { filter })?.children?.length > 0
    },

  // stacked headcount cost
  getStackedHeadcountCost: (_state) => (boardId) => {
    return _state.dataById[boardId]?.stackedHeadcountCost || {}
  },

  getSkillsHeatmap: (_state) => (boardId) => {
    return _state.dataById[boardId]?.skillsHeatmap || {}
  },
  getLayersTable: (_state) => (boardId) => {
    return _state.dataById[boardId]?.layersTable
  }
}

const actions = {
  async fetchLayerAndDepth(
    context,
    {
      boardId,
      filters = [],
      category = null,
      subCategory = null,
      newOrder = null,
      xAxisOrder = null,
      signal
    }
  ) {
    window.mixpanel.track('workforce_hub_fetch_layer_and_depth', {
      boardId,
      filters,
      category,
      subCategory
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchLayerAndDepth', boardId),
      signal
    )
    try {
      const result = await fetchLayerAndDepth({
        boardId,
        filters,
        category,
        subCategory,
        signal: activeSignal
      })
      // Apply Y-axis ordering (series order)
      let modifiedResult = result
      const orderToUse = newOrder || result.categoryOrder
      if (orderToUse) {
        modifiedResult = sortLayerAndDepthYAxis(modifiedResult, orderToUse)
      }
      const xAxisOrderToUse = xAxisOrder || result.xAxisOrder
      // Apply X-axis ordering (data points within series)
      if (xAxisOrderToUse) {
        modifiedResult = sortLayerAndDepthXAxis(modifiedResult, xAxisOrderToUse)
      }

      context.commit('setLayerAndDepth', {
        boardId,
        data: modifiedResult || {}
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchLayerAndDepth err', e.message)
      context.commit('resetLayerAndDepth', boardId)
    } finally {
      complete()
    }
  },

  async fetchLayerAndSpanOfControl(
    context,
    { boardId, filters = [], newOrder, xAxisOrder, category, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_layer_and_span_of_control', {
      boardId,
      filters,
      category
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchLayerAndSpanOfControl', boardId),
      signal
    )
    try {
      const result = await fetchLayerAndSpanOfControl({ boardId, filters, signal: activeSignal })
      const orderToUse = newOrder || result.categoryOrder
      // Apply Y-axis ordering (series order)
      let modifiedResult = result
      if (orderToUse) {
        modifiedResult = reorderLayerAndSpanOfControlYAxis(modifiedResult, orderToUse)
      }
      const xAxisOrderToUse = xAxisOrder || result.xAxisOrder
      // Apply X-axis ordering (data points within series)
      if (xAxisOrderToUse) {
        modifiedResult = reorderLayerAndSpanOfControlXAxis(modifiedResult, xAxisOrderToUse)
      }

      context.commit('setLayerAndSpanOfControl', {
        boardId,
        data: modifiedResult
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchLayerAndSpanOfControl err', e.message)
      context.commit('resetLayerAndSpanOfControl', boardId)
    } finally {
      complete()
    }
  },

  async fetchHeadcountDistribution(
    context,
    { boardId, filters = [], category = null, newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_headcount_distribution', {
      boardId,
      filters,
      category
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchHeadcountDistribution', boardId),
      signal
    )
    try {
      const result = await fetchDistribution({
        boardId,
        filters,
        category,
        signal: activeSignal
      })
      const orgTypesById = context.rootGetters['customFields/orgTypesById'](boardId)

      // Get the category key
      const categoryKey = Object.keys(result.data).find(
        (key) =>
          key.toLowerCase().replaceAll(/\s+/g, '') ===
            category.toLowerCase().replaceAll(/\s+/g, '') ||
          key.toLowerCase().replaceAll(/\s+/g, '') ===
            orgTypesById[category]?.name?.toLowerCase().replaceAll(/\s+/g, '')
      )

      const sortedResult = sortHeadcountDistributionAxis(result, categoryKey, newOrder)
      context.commit('setHeadcountDistribution', { boardId, data: sortedResult })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchHeadcountDistribution err', e.message)
      context.commit('resetHeadcountDistribution', boardId)
    } finally {
      complete()
    }
  },

  async fetchHeadcountCostDistribution(
    context,
    { boardId, filters = [], category = null, isAverages = false, newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_headcount_cost_distribution', {
      boardId,
      filters,
      category,
      isAverages
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchHeadcountCostDistribution', boardId),
      signal
    )
    try {
      const result = await fetchCostDistribution({
        boardId,
        filters,
        category,
        isAverages,
        signal: activeSignal
      })

      if (!result?.data) {
        context.commit('resetHeadcountCostDistribution', boardId)
        return
      }

      const orgTypesById = context.rootGetters['customFields/orgTypesById'](boardId)
      const normalizeKey = (value) =>
        String(value || '')
          .toLowerCase()
          .replaceAll(/\s+/g, '')
      const normalizedCategory = normalizeKey(category)
      const normalizedOrgTypeName = normalizeKey(orgTypesById[category]?.name)

      // Get the category key
      const categoryKey = Object.keys(result.data).find(
        (key) =>
          normalizeKey(key) === normalizedCategory || normalizeKey(key) === normalizedOrgTypeName
      )

      const sortedResult = sortHeadcountCostDistributionAxis(
        result,
        categoryKey,
        newOrder,
        isAverages
      )

      context.commit('setHeadcountCostDistribution', {
        boardId,
        data: sortedResult || {}
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchHeadcountCostDistribution err', e.message)
      context.commit('resetHeadcountCostDistribution', boardId)
    } finally {
      complete()
    }
  },

  async fetchHeadcountGrowthDistribution(
    context,
    { boardId, filters = [], startDate, endDate, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_headcount_growth_distribution', {
      boardId,
      filters,
      startDate,
      endDate
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchHeadcountGrowthDistribution', boardId),
      signal
    )
    try {
      const result = await fetchGrowthDistribution({
        boardId,
        filters,
        startDate,
        endDate,
        signal: activeSignal
      })
      context.commit('setHeadcountGrowthDistribution', { boardId, data: result || {} })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchHeadcountGrowthDistribution err', e.message)
      context.commit('resetHeadcountGrowthDistribution', boardId)
    } finally {
      complete()
    }
  },

  async fetchHeadcountGrowthDistributionDates(context, { boardId, filters, signal }) {
    window.mixpanel.track('workforce_hub_fetch_headcount_growth_distribution_dates', { boardId })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchHeadcountGrowthDistributionDates', boardId),
      signal
    )
    try {
      const result = await fetchGrowthDistributionDates({ boardId, filters, signal: activeSignal })
      context.commit('setHeadcountGrowthDistributionDates', { boardId, data: result || {} })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchHeadcountGrowthDistributionDates err', e.message)
      context.commit('resetHeadcountGrowthDistribution', boardId)
    } finally {
      complete()
    }
  },

  async fetchOrgMap(
    context,
    { boardId, filters = [], category = null, subCategory = null, newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_org_map', {
      boardId,
      filters,
      category,
      subCategory
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchOrgMap', boardId),
      signal
    )
    try {
      const orgData = await fetchOrgmap({
        boardId,
        filters,
        category,
        subCategory,
        signal: activeSignal
      })
      const sortedData = sortOrgMapData(orgData, newOrder, category, subCategory)

      context.commit('setOrgMap', { boardId, data: sortedData || {} })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      logger.error('fetchOrgMap detailed error:', e.message)
      Sentry.captureException(e)
      context.commit('resetOrgMap', boardId)
    } finally {
      complete()
    }
  },

  async fetchIcicleChart(context, { boardId, filters = [], showById, signal }) {
    window.mixpanel.track('workforce_hub_fetch_icicle_chart', { boardId, filters, showById })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchIcicleChart', boardId),
      signal
    )
    try {
      const result = await fetchIcicle({ boardId, filters, showById, signal: activeSignal })

      context.commit('setIcicleChart', {
        boardId,
        data: Object.freeze(result.data) || {},
        groups: result.groups,
        exportableData: result.exportableData
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchIcicleChart err', e.message)
      context.commit('resetIcicleChart', boardId)
    } finally {
      complete()
    }
  },

  async fetchSpanOfControl(context, { boardId, filters = [], category = null, newOrder, signal }) {
    window.mixpanel.track('workforce_hub_fetch_span_of_control', { boardId, filters, category })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchSpanOfControl', boardId),
      signal
    )
    try {
      const result = await fetchSpanOfControl({ boardId, filters, category, signal: activeSignal })
      const sortedResult = sortSpanOfControlData(result, Object.keys(result.data)[0], newOrder)

      context.commit('setSpanOfControl', {
        boardId,
        data: sortedResult
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchSpanOfControl err', e.message)
      context.commit('resetSpanOfControl', boardId)
    } finally {
      complete()
    }
  },

  async fetchAverageSpanOfControl(
    context,
    { boardId, filters = [], category = null, newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_average_span_of_control', {
      boardId,
      filters,
      category
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchAverageSpanOfControl', boardId),
      signal
    )
    try {
      const result = await fetchAverageSpanOfControl({
        boardId,
        filters,
        category,
        signal: activeSignal
      })
      const categoryKey = Object.keys(result.data).find(
        (key) =>
          key.toLowerCase().replace(/\s+/g, '') === category.toLowerCase().replace(/\s+/g, '')
      )
      const sortedResult = sortSpanOfControlData(result, categoryKey, newOrder)

      context.commit('setAverageSpanOfControl', {
        boardId,
        data: sortedResult
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchAverageSpanOfControl err', e.message)
      context.commit('resetAverageSpanOfControl', boardId)
    } finally {
      complete()
    }
  },

  async fetchPyramidChart(
    context,
    { boardId, filters = [], category = null, showBy = 'headcount', newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_pyramid_chart', { boardId, filters, category })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchPyramidChart', boardId),
      signal
    )
    try {
      if (newOrder) {
        let data = context.getters.getPyramidChart(boardId) ?? {}
        if (!data) {
          data = await fetchPyramidChart({
            boardId,
            filters,
            category,
            showBy,
            signal: activeSignal
          })
        }
        const sortedData = {
          actual: sortDataByGivenOrder({
            data: data.data?.actual,
            order: newOrder,
            sortByProperty: 'x'
          }),
          percent: sortDataByGivenOrder({
            data: data.data?.percent,
            order: newOrder,
            sortByProperty: 'x'
          })
        }
        const sortedPlanData = {
          actual: sortDataByGivenOrder({
            data: data.planData?.actual,
            order: newOrder,
            sortByProperty: 'x'
          }),
          percent: sortDataByGivenOrder({
            data: data.planData?.percent,
            order: newOrder,
            sortByProperty: 'x'
          })
        }
        if (sortedData?.actual) applyCorrectness(sortedData.actual)
        if (sortedData?.percent) applyCorrectness(sortedData.percent)
        if (sortedPlanData?.actual) applyCorrectness(sortedPlanData.actual)
        if (sortedPlanData?.percent) applyCorrectness(sortedPlanData.percent)
        context.commit('setPyramidChart', {
          boardId,
          data: { ...data, data: sortedData, planData: sortedPlanData, categoryOrder: newOrder }
        })
        return
      }
      const result = await fetchPyramidChart({
        boardId,
        filters,
        category,
        showBy,
        signal: activeSignal
      })
      context.commit('setPyramidChart', { boardId, data: result || {} })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchPyramidChart err', e.message)
      context.commit('resetPyramidChart', boardId)
    } finally {
      complete()
    }
  },

  async fetchStackedHeadcountCost(
    context,
    { boardId, filters = [], category = null, subCategories = [], newOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_stacked_headcount_cost', {
      boardId,
      filters,
      category,
      subCategories
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchStackedHeadcountCost', boardId),
      signal
    )
    try {
      const result = await fetchStackedHeadcountCost({
        boardId,
        filters,
        category,
        subCategories,
        signal: activeSignal
      })
      const sortedResult = sortStackedHeadcountCostData(
        result,
        Object.keys(result.data)[0],
        newOrder
      )

      context.commit('setStackedHeadcountCost', {
        boardId,
        data: sortedResult
      })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchStackedHeadcountCost err', e.message)
      context.commit('resetStackedHeadcountCost', boardId)
    } finally {
      complete()
    }
  },

  async fetchSkillsHeatmap(
    context,
    { boardId, filters = [], category = null, subCategory = null, newOrder, xAxisOrder, signal }
  ) {
    window.mixpanel.track('workforce_hub_fetch_skills_heatmap', {
      boardId,
      filters,
      category,
      subCategory
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchSkillsHeatmap', boardId),
      signal
    )
    try {
      const result = await fetchSkillsHeatmap({ boardId, filters, signal: activeSignal })
      // Apply Y-axis ordering (rows/roles)
      let modifiedResult = result
      if (newOrder) {
        modifiedResult = sortSkillsHeatmapYAxis(modifiedResult, newOrder)
      }
      // Apply X-axis ordering (columns/people)
      if (xAxisOrder) {
        modifiedResult = sortSkillsHeatmapXAxis(modifiedResult, xAxisOrder)
      }
      context.commit('setSkillsHeatmap', { boardId, data: modifiedResult || {} })
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchSkillsHeatmap err', e.message)
      context.commit('resetSkillsHeatmap', boardId)
    } finally {
      complete()
    }
  },
  async fetchLayersTable(context, { boardId, filters = [], newOrder, columnOrder, signal }) {
    window.mixpanel.track('workforce_hub_fetch_layers_table', {
      boardId,
      filters
    })
    const { signal: activeSignal, complete } = beginRequest(
      getRequestKey('fetchLayersTable', boardId),
      signal
    )
    try {
      const result = await fetchLayersTable({ boardId, filters, signal: activeSignal })

      // Row ordering
      let reorderedResult = { ...result }
      if (newOrder?.length || reorderedResult.categoryOrder?.length) {
        const rowOrderToUse = newOrder || reorderedResult.categoryOrder || []
        reorderedResult = sortLayersTableRows(reorderedResult, rowOrderToUse)
      }

      // Column ordering
      if (columnOrder?.length || reorderedResult.columnOrder?.length) {
        const colOrderToUse = columnOrder || reorderedResult.columnOrder || []
        reorderedResult = sortLayersTableColumns(reorderedResult, colOrderToUse)
      }

      context.commit('setLayersTable', {
        boardId,
        data: reorderedResult
      })
      return
    } catch (e) {
      if (isCanceledError(e)) {
        return
      }
      Sentry.captureException(e)
      logger.error('fetchLayersTable err', e.message)
      context.commit('resetLayersTable', boardId)
    } finally {
      complete()
    }
  },

  resetHeadcountCostDistribution(context, boardId) {
    try {
      context.commit('resetHeadcountCostDistribution', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetHeadcountCostDistribution err', e.message)
    }
  },

  resetLayerAndDepth(context, boardId) {
    try {
      context.commit('resetLayerAndDepth', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetLayerAndDepth err', e.message)
    }
  },

  resetLayerAndSpanOfControl(context, boardId) {
    try {
      context.commit('resetLayerAndSpanOfControl', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetLayerAndSpanOfControl err', e.message)
    }
  },

  resetIcicleChart(context, boardId) {
    try {
      context.commit('resetIcicleChart', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetIcicleChart err', e.message)
    }
  },

  resetHeadcountGrowthDistribution(context, boardId) {
    try {
      context.commit('resetHeadcountGrowthDistribution', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetHeadcountGrowthDistribution err', e.message)
    }
  },

  resetSpanOfControl(context, boardId) {
    try {
      context.commit('resetSpanOfControl', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetSpanOfControl err', e.message)
    }
  },

  resetAverageSpanOfControl(context, boardId) {
    try {
      context.commit('resetAverageSpanOfControl', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetAverageSpanOfControl err', e.message)
    }
  },

  resetOrgMap(context, boardId) {
    try {
      context.commit('resetOrgMap', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetOrgMap err', e.message)
    }
  },

  resetPyramidChart(context, boardId) {
    try {
      context.commit('resetPyramidChart', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetPyramidChart err', e.message)
    }
  },

  resetHeadcountDistribution(context, boardId) {
    try {
      context.commit('resetHeadcountDistribution', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetHeadcountDistribution err', e.message)
    }
  },

  resetStackedHeadcountCost(context, boardId) {
    try {
      context.commit('resetStackedHeadcountCost', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetStackedHeadcountCost err', e.message)
    }
  },

  resetSkillsHeatmap(context, boardId) {
    try {
      context.commit('resetSkillsHeatmap', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetSkillsHeatmap err', e.message)
    }
  },
  resetLayersTable(context, boardId) {
    try {
      context.commit('resetLayersTable', boardId)
    } catch (e) {
      Sentry.captureException(e)
      logger.error('resetLayersTable err', e.message)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
  modules: {}
}
